import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Heart, Users, Sparkles, Shield, Star } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta } from "../components/shared/SEOHelper";

const YogaRetreatForBeginners = () => {
  usePageMeta({
    title: "Yoga Retreat for Beginners UK | First Time Yoga Retreat",
    description: "Perfect yoga retreats for beginners in the UK. No experience needed. Supportive environment, accessible practices, and all the guidance you need for your",
    canonical: "https://annelielily.com/retreats/beginners"
  });

  const concerns = [
    {
      worry: "I'm not flexible enough",
      reality: "Yoga isn't about touching your toes — it's about what happens on the way down. Every pose can be modified, and props (blocks, straps, cushions) make everything accessible.",
      icon: Sparkles
    },
    {
      worry: "I won't be able to keep up",
      reality: "Our retreats aren't about 'keeping up'. You're encouraged to rest whenever you need to, skip anything that doesn't feel right, and go at your own pace throughout.",
      icon: Heart
    },
    {
      worry: "Everyone else will be advanced",
      reality: "Most retreat guests are not yoga teachers or gymnasts! You'll find a mix of levels, and many people come as complete beginners. Everyone starts somewhere.",
      icon: Users
    },
    {
      worry: "I'll feel awkward or judged",
      reality: "The retreat environment is specifically designed to be welcoming and non-judgmental. Everyone is focused on their own experience, and the atmosphere is supportive.",
      icon: Shield
    }
  ];

  const whyRetreatsGreatForBeginners = [
    "Extended time allows you to actually learn and absorb (vs rushing through a class)",
    "Small group size means personal attention and modifications for your body",
    "Away from daily life, you can focus entirely on the experience",
    "Multiple sessions let you build on what you learn throughout the day",
    "Supportive environment with others who understand the learning journey",
    "Trying different styles helps you discover what resonates with you"
  ];

  const whatYouWillLearn = [
    { title: "Basic Postures", desc: "Foundation poses like Downward Dog, Warrior, Child's Pose — with proper alignment" },
    { title: "Breath Awareness", desc: "How to breathe properly in yoga and use breath to deepen your practice" },
    { title: "Mindfulness", desc: "Present-moment awareness that you can carry into everyday life" },
    { title: "Relaxation", desc: "Deep rest techniques including yoga nidra (guided relaxation)" },
    { title: "Self-Compassion", desc: "How to approach your body with kindness rather than judgment" },
    { title: "Home Practice Basics", desc: "Simple sequences you can continue after the retreat" }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <Navigation />
      <PageBreadcrumb currentPage="Retreats for Beginners" currentSlug="retreats/beginners" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-light text-neutral-800 mb-5">
              Yoga Retreats for Beginners
            </h1>
            <p className="text-neutral-700 max-w-2xl mx-auto leading-relaxed">
              Never done yoga before? That's completely fine — in fact, a retreat might be 
              the perfect place to start. Here's everything you need to know about joining 
              your first yoga retreat as a complete beginner.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reassurance */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              You Don't Need Any Experience
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                Let's be clear: <strong>you don't need to be able to touch your toes, stand 
                on your head, or have ever set foot in a yoga studio</strong> to come on retreat. 
                Many of our guests are complete beginners, and some tell us it was the best 
                way to discover yoga.
              </p>
              <p>
                A retreat gives you time and space to learn properly — not rushing through 
                poses in a packed class, but moving slowly, with guidance, in a supportive 
                environment. If anything, beginners often benefit <em>more</em> from retreats 
                than experienced yogis.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Common Concerns */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Common Beginner Concerns (And Why They're Not a Problem)
            </h2>
          </motion.div>

          <div className="space-y-6">
            {concerns.map((item, i) => (
              <motion.div
                key={item.worry}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/80 p-6 rounded-2xl border border-gold/10"
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center">
                      <item.icon className="w-6 h-6 text-gold" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-neutral-800 mb-2">
                      "I'm worried that {item.worry.toLowerCase()}"
                    </p>
                    <p className="text-neutral-600">{item.reality}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Retreats Are Great for Beginners */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Why Retreats Are Perfect for Starting Yoga
            </h2>
            <p className="text-neutral-700 mb-6">
              Many yoga teachers actually recommend retreats for beginners over regular 
              classes. Here's why:
            </p>
            <ul className="space-y-3">
              {whyRetreatsGreatForBeginners.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  <span className="text-neutral-700">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What You'll Learn */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              What You'll Learn as a Beginner
            </h2>
            <p className="text-neutral-600 max-w-xl mx-auto">
              By the end of a retreat, you'll have a solid foundation in yoga that you can 
              build on for years to come.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {whatYouWillLearn.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white/80 p-5 rounded-xl border border-gold/10"
              >
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{item.title}</h3>
                <p className="text-neutral-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tips for Beginners */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              Tips for Your First Yoga Retreat
            </h2>
            <div className="space-y-4">
              {[
                {
                  tip: "Tell the teacher you're a beginner",
                  detail: "They'll keep an eye out for you and offer extra guidance. No shame in being new!"
                },
                {
                  tip: "Arrive with an open mind, not expectations",
                  detail: "Let go of ideas about what yoga 'should' look like. Your experience will be unique."
                },
                {
                  tip: "Listen to your body above all else",
                  detail: "If something hurts or feels wrong, stop. Yoga should feel good, not painful."
                },
                {
                  tip: "Rest when you need to",
                  detail: "Child's Pose is always available. Taking rest is a sign of wisdom, not weakness."
                },
                {
                  tip: "Don't compare yourself to others",
                  detail: "Everyone's body is different. Focus on your own experience and progress."
                },
                {
                  tip: "Ask questions",
                  detail: "If you don't understand something, ask. Teachers love curious beginners."
                }
              ].map((item, i) => (
                <div key={i} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h4 className="font-medium text-neutral-800 mb-1">{item.tip}</h4>
                  <p className="text-neutral-600 text-sm">{item.detail}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-8 rounded-2xl border border-gold/10 text-center"
          >
            <div className="flex justify-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-gold text-gold" />
              ))}
            </div>
            <p className="text-neutral-700 italic text-lg mb-6">
              "I'd never done yoga before and was really nervous about the retreat. But 
              from the moment I arrived, I felt welcome. Annelie explained everything 
              clearly and made sure I was comfortable throughout. I've now been practicing 
              regularly for a year — it changed my life."
            </p>
            <p className="font-medium text-neutral-800">First-time retreat guest</p>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Begin Your Yoga Journey?
            </h2>
            <p className="text-neutral-600 mb-6">
              Our retreats welcome beginners warmly. Explore upcoming dates or get in 
              touch with any questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/retreats/yoga-retreats-uk" className="btn-primary">
                View Yoga Retreats UK <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <Link to="/yoga-classes/beginner-yoga" className="btn-secondary">
                Beginner Yoga Classes
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <h3 className="font-heading text-lg text-neutral-800 mb-4 text-center">Helpful Resources</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <Link to="/retreats/what-to-expect" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">What to Expect</span>
              <p className="text-neutral-500 text-xs mt-1">Full retreat guide</p>
            </Link>
            <Link to="/retreats/packing-list" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Packing List</span>
              <p className="text-neutral-500 text-xs mt-1">What to bring</p>
            </Link>
            <Link to="/yoga-classes/beginner-yoga" className="bg-white/70 p-4 rounded-xl border border-gold/10 hover:border-gold/30 transition-all text-center">
              <span className="text-neutral-800 font-medium text-sm">Beginner Classes</span>
              <p className="text-neutral-500 text-xs mt-1">Weekly in Frome</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default YogaRetreatForBeginners;
